/**
 * TypeScript mirror of the sky noise in shaders/sky.frag.
 *
 * Lattice values come from the pcg3d integer hash (Jarzynski and Olano 2020), which GLSL ES 3.00
 * evaluates with the same wrapping 32-bit arithmetic as Math.imul here, so the mirror reproduces
 * the shader's cloud field up to float32 interpolation error. Coverage calibration and the tests
 * depend on that.
 */

import { pcg3dX } from './latticeHash';

/** Keeps lattice coordinates positive before the int to uint conversion in GLSL. */
const LATTICE_OFFSET = 65536;
const TWO_POW_32 = 4294967296;
const OCTAVE_SHIFT: readonly [number, number, number] = [19.1, 7.3, 11.7];
const OCTAVE_SEED_STEP = 101;

export const MASS_OCTAVES = 5;
export const STREAK_OCTAVES = 3;
/*
 * Standard deviation of the three-octave fbm about 0.5, measured on this mirror; the streak, warp
 * and front fields divide by it to read in standard deviations.
 */
export const STREAK_FBM_STD = 0.125;

function latticeValue(ix: number, iy: number, iz: number, seed: number): number {
  const hx = (ix + LATTICE_OFFSET + seed) >>> 0;
  return pcg3dX(hx, iy + LATTICE_OFFSET, iz + LATTICE_OFFSET) / TWO_POW_32;
}

function fade(t: number): number {
  return t * t * (3 - 2 * t);
}

function mix(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

export function valueNoise3(x: number, y: number, z: number, seed: number): number {
  const ix = Math.floor(x);
  const iy = Math.floor(y);
  const iz = Math.floor(z);
  const ux = fade(x - ix);
  const uy = fade(y - iy);
  const uz = fade(z - iz);
  const c = (dx: number, dy: number, dz: number) => latticeValue(ix + dx, iy + dy, iz + dz, seed);
  const y0 = mix(mix(c(0, 0, 0), c(1, 0, 0), ux), mix(c(0, 1, 0), c(1, 1, 0), ux), uy);
  const y1 = mix(mix(c(0, 0, 1), c(1, 0, 1), ux), mix(c(0, 1, 1), c(1, 1, 1), ux), uy);
  return mix(y0, y1, uz);
}

/** Octave sum normalised to [0, 1]; gain 0.5, lacunarity 2. */
export function fbm3(x: number, y: number, z: number, seed: number, octaves: number): number {
  let total = 0;
  let amplitude = 1;
  let norm = 0;
  let frequency = 1;
  for (let o = 0; o < octaves; o++) {
    total +=
      amplitude *
      valueNoise3(
        x * frequency + o * OCTAVE_SHIFT[0],
        y * frequency + o * OCTAVE_SHIFT[1],
        z * frequency + o * OCTAVE_SHIFT[2],
        seed + o * OCTAVE_SEED_STEP
      );
    norm += amplitude;
    amplitude *= 0.5;
    frequency *= 2;
  }
  return total / norm;
}
